import { readFileSync } from 'node:fs';
import { createServer } from 'node:http';

import { GpmPayConfigError, GpmPayWebhookSignatureError } from '../../core/errors.js';
import {
  SIGNATURE_HEADER,
  assertWebhookSignature,
  constructWebhookEvent,
} from '../../webhooks/verify.js';
import { secretFromFlags } from '../context.js';
import {
  webhookHistory,
  webhookRetry,
  webhookSettings,
  type WebhookAdminFlags,
} from './webhook-admin.js';
import { sendWebhook, type WebhookSendFlags } from './webhook-send.js';
import { EXIT, fields, money, out, style, symbols } from '../ui.js';

export interface WebhookFlags extends WebhookAdminFlags, WebhookSendFlags {
  signature?: string | undefined;
  port?: string | undefined;
}

function readStdin(): Promise<string> {
  return new Promise((resolve, reject) => {
    let data = '';
    // setEncoding makes the chunks strings, but the typings still say Buffer.
    process.stdin.setEncoding('utf8');
    process.stdin.on('data', (chunk: string | Buffer) => {
      data += typeof chunk === 'string' ? chunk : chunk.toString('utf8');
    });
    process.stdin.on('end', () => resolve(data));
    process.stdin.on('error', reject);
  });
}

async function verifyOnce(flags: WebhookFlags): Promise<number> {
  const secret = secretFromFlags(flags);

  if (flags.signature === undefined || flags.signature === '') {
    throw new GpmPayConfigError(
      `Missing --signature. Pass the ${SIGNATURE_HEADER} header value, e.g. "t=1785600000,v1=abc…".`,
      'invalid_argument',
    );
  }

  const rawBody =
    flags.file === undefined ? await readStdin() : readFileSync(flags.file, 'utf8');

  try {
    const { timestamp } = assertWebhookSignature({
      rawBody,
      signature: flags.signature,
      secret,
    });
    out(`${symbols.ok()} Signature valid ${style.dim(`(t=${String(timestamp)})`)}`);
    return EXIT.OK;
  } catch (error) {
    if (error instanceof GpmPayWebhookSignatureError) {
      out(`${symbols.fail()} ${style.red(error.reason)}: ${error.message}`);
      return EXIT.ERROR;
    }
    throw error;
  }
}

function listen(flags: WebhookFlags): Promise<number> {
  const secret = secretFromFlags(flags);
  const port = Number(flags.port ?? '4444');

  return new Promise((resolve, reject) => {
    const server = createServer((req, res) => {
      const chunks: Buffer[] = [];
      req.on('data', (chunk: Buffer) => chunks.push(chunk));
      req.on('end', () => {
        const rawBody = Buffer.concat(chunks);
        const signature = req.headers[SIGNATURE_HEADER.toLowerCase()];

        try {
          const event = constructWebhookEvent({
            rawBody,
            signature: Array.isArray(signature) ? (signature[0] ?? '') : (signature ?? ''),
            secret,
            headers: req.headers,
          });

          const payload = event.payload;
          out('');
          out(`${symbols.ok()} ${style.green(event.type)}`);
          out(
            fields([
              ['Transaction', payload.id],
              ['Gateway', payload.gateway],
              ['Direction', payload.transferType === 'in' ? style.green('IN') : 'OUT'],
              ['Amount', money(payload.transferAmount)],
              ['Content', payload.content],
              ['Reference', payload.referenceCode],
              ['Source', payload.source],
            ]),
          );

          res.writeHead(200, { 'content-type': 'application/json' });
          res.end(JSON.stringify({ received: true }));
        } catch (error) {
          const reason =
            error instanceof GpmPayWebhookSignatureError ? error.reason : 'error';
          out('');
          out(`${symbols.fail()} ${style.red('rejected')} — ${reason}`);
          out(style.dim(`  ${(error as Error).message}`));
          res.writeHead(401, { 'content-type': 'application/json' });
          res.end(JSON.stringify({ error: reason }));
        }
      });
    });

    server.on('error', reject);
    server.listen(port, () => {
      out(`${symbols.ok()} Listening for GPM Pay webhooks on ${style.cyan(`http://localhost:${String(port)}`)}`);
      out('');
      out(style.dim('  Expose it and register the public URL, e.g.:'));
      out(style.dim(`    ngrok http ${String(port)}`));
      out(
        style.dim(
          '    await client.webhookSettings.createHmacEndpoint({ url: "https://<id>.ngrok.app" })',
        ),
      );
      out('');
      out(style.dim('  Press Ctrl+C to stop.'));
    });

    process.on('SIGINT', () => {
      server.close(() => resolve(EXIT.OK));
    });
  });
}

export async function webhookCommand(
  subcommand: string | undefined,
  positional: string[],
  flags: WebhookFlags,
): Promise<number> {
  switch (subcommand) {
    case 'verify':
      return await verifyOnce(flags);
    case 'listen':
      return await listen(flags);
    case 'send':
      return await sendWebhook(flags);
    case 'settings':
      return await webhookSettings(flags);
    case 'history':
      return await webhookHistory(flags);
    case 'retry':
      return await webhookRetry(positional[0], flags);
    default:
      throw new GpmPayConfigError(
        `Unknown subcommand "webhook ${subcommand ?? ''}". ` +
          'Expected: send | listen | verify | settings | history | retry.',
        'invalid_argument',
      );
  }
}
